import { useEffect, useState, useMemo } from "react";
import { Calendar, Tag, Github, BookOpen, Code, Settings } from "lucide-react";
import "../styles/Latest.css";


const GITHUB_USER = "yousefbh1";


const Latest = () => {
  /* ---------------- GitHub activity ---------------- */
  const [githubUpdates, setGithubUpdates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    /* Hit the public “events” feed (30 latest) */
    const endpoint = `https://api.github.com/users/${GITHUB_USER}/events/public`;

    fetch(endpoint)
      .then((r) => r.json())
      .then((events) => {
        /**
         * Reduce the noisy event stream down to
         * commit, PR, issue events.
         */
        const mapped = events
          .filter((e) =>
            ["PushEvent", "PullRequestEvent", "IssuesEvent"].includes(e.type)
          )
          .slice(0, 10) // keep newest 10
          .map((e, idx) => {
            // Normalise to your display shape
            if (e.type === "PushEvent") {
              const commit = e.payload.commits.at(-1); // last commit message
              return {
                id: idx,
                type: "commit",
                repo: e.repo.name.split("/")[1],
                title: commit?.message.split("\n")[0] ?? "Commit",
                description: commit?.message,
                date: e.created_at.slice(0, 10),
                url: `https://github.com/${e.repo.name}/commit/${commit?.sha}`,
              };
            }
            if (e.type === "PullRequestEvent") {
              const pr = e.payload.pull_request;
              return {
                id: idx,
                type: "pull_request",
                repo: e.repo.name.split("/")[1],
                title: pr.title,
                description: pr.body,
                date: e.created_at.slice(0, 10),
                url: pr.html_url,
              };
            }
            if (e.type === "IssuesEvent") {
              const issue = e.payload.issue;
              return {
                id: idx,
                type: "issue",
                repo: e.repo.name.split("/")[1],
                title: issue.title,
                description: issue.body,
                date: e.created_at.slice(0, 10),
                url: issue.html_url,
              };
            }
            return null;
          })
          .filter(Boolean);

        setGithubUpdates(mapped);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  /* ------------- in development ----------------- */

  const devTopics = useMemo(
    () => [
      {
        id: 1,
        title: "Building a highly scalable data-intensive system",
        description:
          "Working on a highly scalable data-intensive web application, using microservices, that can support tens of thousands of concurrent users, with the help of technologies such as: RabbitMQ, gRPC, Valkey/Redis, SQL and NoSQL databases, and CI/CD testing workflow",
        tags: ["Systems", "Fullstack", "Backend", "Web/Networking", "SQL", "NoSQL"],
        resources: [
          { name: "System Design Roadmap", url: "https://www.scholarhat.com/tutorial/systemdesign/system-design-roadmap" },
          { name: "DDIA", url: "https://github.com/keyvanakbary/learning-notes/blob/master/books/designing-data-intensive-applications.md" },
        ],
      },
      {
        id: 2,
        title: "AI Video Editor",
        description:
          "Building an AI video editor, which can take in an mp4, and a list of commands: such as cut the first 3 seconds, rotate horizontally from 12 seconds to 16 seconds, etc, and executes accordinly",
        tags: ["AI/ML", "Python", "Backend", "Fullstack", "Frontend"],
        resources: [
          { name: "FFMPEG Library", url: "https://www.ffmpeg.org/ffmpeg.html" },
        ],
      },
      {
        id: 3,
        title: "Custom WebSocket Library",
        description:
          "Working on implementing a custom WebSocket library in Go",
        tags: ["Golang", "Web/Networking", "Systems"],
        resources: [
          { name: "WebSockets", url: "https://en.wikipedia.org/wiki/WebSocket" },
        ],
      },
    ],
    []
  );


  /* -------------- research topics -------------- */
  const researchTopics = useMemo(
    () => [
      {
        id: 1,
        title: "Reading Kleppman's Designing Data-Intensive Applications",
        description:
          "Learning how large, data-intensive distributued applications are built and maintained, and how to make the best infrastructure choices depending on the use case",
        tags: ["Systems", "Backend", "Web/Networking", "SQL", "NoSQL"],
        resources: [
          { name: "Research Paper", url: "#" },
          { name: "Helpful Github Summary", url: "https://github.com/keyvanakbary/learning-notes/blob/master/books/designing-data-intensive-applications.md" },
        ],
      },
      {
        id: 2,
        title: "WebAssembly and the Future of Web Development",
        description:
          "Researching how WebAssembly is changing the landscape of web development and enabling new use cases.",
        tags: ["WASM", "Web/Networking", "Performance"],
        resources: [
          { name: "Demo", url: "#" },
          { name: "Tutorial", url: "#" },
        ],
      },
      {
        id: 3,
        title: "Learning Golang",
        description:
          "Learning Golang to help with managing concurrency and unit tests more efficiently in backend applications",
        tags: ["Golang", "Web/Networking", "Systems"],
        resources: [
          { name: "Learn Go by Building", url: "https://gophercises.com/" },
          { name: "Learn Go with Tests", url: "https://quii.gitbook.io/learn-go-with-tests" },
        ],
      },
    ],
    []
  );

  /* ------------------- render -------------------- */
  return (
    <div className="latest-container">
      <h1 className="section-title">Latest Updates</h1>

      {/* ---------- Project topics ---------- */}
      <section className="research-topics">
        <h2 className="subsection-title">
          <Settings className="subsection-icon" />
          In Development
        </h2>

        <div className="topics-grid">
          {devTopics.map((topic) => (
            <div key={topic.id} className="topic-card card">
              <h3 className="topic-title">{topic.title}</h3>
              <p className="topic-description">{topic.description}</p>

              <div className="topic-tags">
                {topic.tags.map((tag) => (
                  <span key={tag} className="tag">
                    <Tag size={12} /> {tag}
                  </span>
                ))}
              </div>

              <div className="topic-resources">
                <h4>Resources:</h4>
                <ul>
                  {topic.resources.map((resource, i) => (
                    <li key={i}>
                      <a
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {resource.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- Research topics ---------- */}
      <section className="research-topics">
        <h2 className="subsection-title">
          <BookOpen className="subsection-icon" />
          Researching
        </h2>

        <div className="topics-grid">
          {researchTopics.map((topic) => (
            <div key={topic.id} className="topic-card card">
              <h3 className="topic-title">{topic.title}</h3>
              <p className="topic-description">{topic.description}</p>

              <div className="topic-tags">
                {topic.tags.map((tag) => (
                  <span key={tag} className="tag">
                    <Tag size={12} /> {tag}
                  </span>
                ))}
              </div>

              <div className="topic-resources">
                <h4>Resources:</h4>
                <ul>
                  {topic.resources.map((resource, i) => (
                    <li key={i}>
                      <a
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {resource.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- GitHub timeline ---------- */}
      <section className="github-updates">
        <h2 className="subsection-title">
          <Github className="subsection-icon" />
          Recent GitHub Activity
        </h2>

        {loading && <p className="loading">Fetching activity…</p>}

        {!loading && githubUpdates.length === 0 && (
          <p className="empty">No recent public events for {GITHUB_USER}.</p>
        )}

        <div className="timeline">
          {githubUpdates.map((update) => (
            <div key={update.id} className="timeline-item">
              <div className="timeline-marker" />
              <div className="timeline-content card">
                <div className="update-header">
                  <span className={`update-type ${update.type}`}>
                    {update.type === "commit" && <Code size={16} />}
                    {update.type === "pull_request" && <Github size={16} />}
                    {update.type === "issue" && <BookOpen size={16} />}
                    {update.type}
                  </span>
                  <span className="update-date">
                    <Calendar size={14} /> {update.date}
                  </span>
                </div>

                <h3 className="update-title">
                  <a
                    href={update.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {update.title}
                  </a>
                </h3>

                <div className="update-repo">
                  in <span>{update.repo}</span>
                </div>

                {update.description && (
                  <p className="update-description">{update.description}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Latest;
